import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Modal from '../components/Modal';
import { inr, num, dt, day, apiError, shelfLifeToDate } from '../utils/format';
import { useAuth } from '../context/AuthContext';
import { downloadCSVSections } from '../utils/csv';
import './Production.css';

const urgencyBadge = (u) => ({ overdue: 'badge-red', high: 'badge-red', medium: 'badge-gold', low: 'badge-green', none: 'badge-gray' }[u] || 'badge-gray');

const IconUser = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="14" height="14"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const IconChevron = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M9 6l6 6-6 6"/></svg>;

// ── Items to prepare: list + right-drawer detail (requests & ingredients) ──
const PlanItems = ({ plan, onStart, canStart }) => {
  const d = plan;
  const [selected, setSelected] = useState(null);   // product open in the detail modal
  const [showAll, setShowAll] = useState(false);
  const sel = selected && d ? d.products.find((p) => p.productId === selected) : null;
  const selRemaining = sel ? Number(sel.remaining ?? 0) : 0;
  const selBatches = sel ? (sel.producedBatches || []) : [];   // batches linked to this item's open requests
  // Only items with bulk still to make. Once fully produced (outstanding ≤ 0) the
  // kitchen is done with it — it moves to Completed and awaits warehouse acceptance,
  // even though the order stays 'partial' until then.
  const list = d ? d.products.filter((p) => Number(p.bulkKg) > 0) : [];
  const shownList = showAll ? list : list.slice(0, 3);
  return (
    <>
      {!d ? <div className="spinner" /> : (
        list.length ? (<>
          <div className="pcol-stack">
            {shownList.map((p) => {
              const requested = Number(p.ordered || 0);
              const made = Number(p.made || 0);
              const making = Number(p.making || 0);
              const remaining = Number(p.remaining ?? 0);
              return (
                <div key={p.productId} className="pcard" style={{ cursor: 'pointer', ...(selected === p.productId ? { boxShadow: '0 0 0 2px #c9a227 inset' } : {}) }} onClick={() => setSelected(p.productId)}>
                  <div className="pcard-top">
                    <span className="pcard-code">{(p.requests || []).map((r) => r.code).join(', ') || '—'}</span>
                    <span className="pcard-tag">none</span>
                  </div>
                  <div className="pcard-title">{p.product}</div>
                  <div className="pcard-badges">
                    {p.recipe ? (p.canProduce ? <span className="badge badge-green">Ready</span> : <span className="badge badge-red">Short</span>) : <span className="badge badge-red">No recipe</span>}
                    <span className="pchip">{p.unit || 'kg'}</span>
                  </div>
                  <div className="pcard-row">
                    <div>
                      <div className="pcard-qty">{num(requested)} {p.unit || 'kg'} to make</div>
                      <div className="pcard-sub">Requested</div>
                    </div>
                    <span className="pcard-made">Made {num(made)} {p.unit || 'kg'}</span>
                  </div>
                  {p.master && <div className="pcard-owner"><IconUser /> {p.master}</div>}
                  {canStart && making === 0 && remaining > 0 && (
                    <button className="btn btn-primary btn-block" style={{ marginTop: '.6rem' }} onClick={(e) => { e.stopPropagation(); onStart(p, remaining); }}>Start batch</button>
                  )}
                </div>
              );
            })}
          </div>
          {list.length > 3 && (
            <button className="pcol-viewall" onClick={() => setShowAll((s) => !s)}>{showAll ? 'Show less' : `View All Pending (${list.length})`} <IconChevron /></button>
          )}
        </>) : <div className="pcol-empty">Nothing pending to prepare.</div>
      )}

      {sel && (
        <Modal title={sel.product} onClose={() => setSelected(null)} wide
          footer={<>
            <button className="btn btn-outline" onClick={() => setSelected(null)}>Close</button>
            {canStart && (Number(sel.making || 0) > 0
              ? <span className="badge badge-gold">Batch in progress</span>
              : selRemaining <= 0
                ? <span className="badge badge-green">Fully made</span>
                : <button className="btn btn-primary" onClick={() => { onStart(sel, selRemaining); setSelected(null); }}>Start batch</button>)}
          </>}>
          <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <div>{sel.recipe ? <Link to={`/recipes/${sel.recipe.id}`} className="link-strong">{sel.recipe.name}</Link> : <span className="badge badge-red">No recipe</span>}</div>
            <div><span className="badge badge-gold">{num(sel.bulkKg)} {sel.unit || 'kg'} to make</span> <span className={`badge ${urgencyBadge(sel.urgency)}`} style={{ marginLeft: 6 }}>{sel.urgency}</span></div>
          </div>
          {sel.batches != null && (
            <p className="muted small mt" style={{ marginBottom: 0 }}>
              <strong>{sel.batches} batch{sel.batches === 1 ? '' : 'es'}</strong> to fulfil this item{sel.recipe?.batchKg ? ` — one batch makes ${num(sel.recipe.batchKg)} kg` : ''}.
            </p>
          )}

          {selBatches.length > 0 && (
            <>
              <strong className="small" style={{ display: 'block', marginTop: '1rem' }}>Batches made</strong>
              <div className="table-wrap">
                <table className="grid" style={{ marginTop: '.3rem' }}>
                  <thead><tr><th>Batch</th><th className="right">Amount</th><th className="right">Wastage</th><th>Status</th><th>When</th></tr></thead>
                  <tbody>
                    {selBatches.map((b) => {
                      const u = b.outputUnit || 'kg';
                      return (
                        <tr key={b.id}>
                          <td className="mono">{b.code}</td>
                          <td className="right">{b.status === 'ready' ? `${num(b.producedQty)} ${u}` : `${num(b.outputQty)} ${u}`}</td>
                          <td className="right">{b.status === 'ready' && Number(b.wastageQty) > 0 ? <span className="neg">{num(b.wastageQty)} {u}</span> : '—'}</td>
                          <td><span className={`badge ${b.status === 'ready' ? 'badge-green' : b.status === 'cancelled' ? 'badge-red' : 'badge-gold'}`}>{b.status === 'ready' ? 'made' : b.status === 'in_production' ? 'making' : b.status}</span></td>
                          <td className="muted">{dt(b.readyAt || b.createdAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {(Number(sel.making || 0) > 0 || Number(sel.made || 0) > 0) && (
            <p className="muted small mt">
              {Number(sel.making || 0) > 0 && <>Making {num(sel.making)} kg. </>}
              {Number(sel.made || 0) > 0 && <>Made {num(sel.made)} kg. </>}
              {selRemaining > 0 ? `${num(selRemaining)} kg still required.` : 'Requirement met.'}
            </p>
          )}
          <div className="stat-row mt" style={{ gap: '.6rem' }}>
            <div className="card card-pad stat"><div className="stat-label">Total made</div><div className="stat-value">{num(sel.made || 0)} kg</div></div>
            <div className="card card-pad stat"><div className="stat-label">Wastage recorded</div><div className="stat-value">{num(sel.wasted || 0)} kg</div></div>
            <div className="card card-pad stat"><div className="stat-label">Available to transfer</div><div className="stat-value">{num(sel.available || 0)} kg</div><div className="muted small">production-ready stock on hand</div></div>
          </div>

          <strong className="small" style={{ display: 'block', marginTop: '1rem' }}>Kitchen orders</strong>
          <div className="table-wrap">
            <table className="grid" style={{ marginTop: '.3rem' }}>
              <thead><tr><th>Order</th><th className="right">Ordered</th><th className="right">Delivered</th><th>Needed by</th></tr></thead>
              <tbody>
                {sel.requests.map((r, i) => (
                  <tr key={i}><td className="mono">{r.code}</td><td className="right">{num(r.qtyOrdered)} {r.unit}</td><td className="right">{num(r.qtyReceived)}</td><td className="muted">{r.needByDate ? day(r.needByDate) : '—'}</td></tr>
                ))}
                {!sel.requests.length && <tr><td colSpan={4} className="empty">No open kitchen orders.</td></tr>}
              </tbody>
            </table>
          </div>

          <strong className="small" style={{ display: 'block', marginTop: '1rem' }}>Ingredients needed</strong>
          <div className="table-wrap">
            <table className="grid" style={{ marginTop: '.3rem' }}>
              <thead><tr><th>Material</th><th className="right">Required</th><th className="right">Available</th><th className="right">Short</th></tr></thead>
              <tbody>
                {sel.ingredients.map((ing, i) => <tr key={i}><td>{ing.name}</td><td className="right">{num(ing.required)} {ing.unit}</td><td className="right">{num(ing.available)}</td><td className="right">{ing.shortfall > 0 ? <span className="neg">{num(ing.shortfall)}</span> : '—'}</td></tr>)}
                {!sel.ingredients.length && <tr><td colSpan={4} className="empty">No ingredients on this recipe.</td></tr>}
              </tbody>
            </table>
          </div>
        </Modal>
      )}
    </>
  );
};

// ── Board cards: In Progress / Completed / Rework ──
const ProgressCard = ({ b, canWrite, onDone }) => {
  const unit = b.outputUnit || 'kg';
  const target = Number(b.outputQty || 0);
  const costPerUnit = target ? Number(b.totalCost || 0) / target : null;
  return (
    <div className="pcard pcard--progress">
      <div className="pcard-top"><span className="pcard-code">{b.code}</span><span className="pcard-tag pcard-tag--prod">In Production</span></div>
      <div className="pcard-title">{b.product?.name || b.rawMaterial?.name}</div>
      <div className="pcard-badges"><span className="pchip">{unit}</span><span className="pcard-qty">{num(target)} {unit} to make</span></div>
      {costPerUnit != null && Number(b.totalCost) > 0 && <div className="pcard-sub">{inr(costPerUnit)} per {unit}</div>}
      <div className="pcard-owner"><IconUser /> {b.creator?.name || 'Owner'} · {dt(b.createdAt)}</div>
      {canWrite && <button className="btn btn-primary pcard-done-btn" onClick={() => onDone(b)}>✓ Mark as Done</button>}
    </div>
  );
};
const CompletedCard = ({ b }) => {
  const unit = b.outputUnit || 'kg';
  const made = Number(b.producedQty || 0);
  const costPerUnit = made ? Number(b.totalCost || 0) / made : null;
  return (
    <div className="pcard">
      <div className="pcard-top"><span className="pcard-code">{b.code}</span><span className="pcard-tag pcard-tag--ready">Ready</span></div>
      <div className="pcard-title">{b.product?.name || b.rawMaterial?.name}</div>
      <div className="pcard-badges"><span className="pchip">{unit}</span><span className="pcard-qty">{num(made)} {unit} made</span></div>
      <div className="pcard-sub">{inr(b.totalCost)}{costPerUnit != null ? ` · ${inr(costPerUnit)}/${unit}` : ''}</div>
      <div className="pcard-owner"><IconUser /> {b.creator?.name || 'Owner'} · {dt(b.readyAt || b.createdAt)}</div>
    </div>
  );
};
const ReworkCard = ({ r }) => (
  <div className="pcard pcard--rework">
    <div className="pcard-top"><span className="pcard-code">{r.returnCode || r.code || '—'}</span></div>
    <div className="pcard-title">{r.product}</div>
    <div className="pcard-badges"><span className="pchip">{r.variant || r.unit || '—'}</span><span className="pcard-qty">{num(r.qty)} {r.unit}</span></div>
    <div className="pcard-sub">{r.reason || '—'}</div>
    <div className="pcard-owner"><IconUser /> {r.branch || 'Warehouse'}{r.when ? ` · ${dt(r.when)}` : ''}</div>
  </div>
);

// ── Download: everything still to make + consolidated raw materials ──
const qty3 = (n) => Math.round((Number(n) || 0) * 1000) / 1000;   // plain number so Excel sums it
const exportPlanCSV = (plan) => {
  const items = (plan?.products || []).filter((p) => Number(p.bulkKg) > 0);
  const status = (p) => (!p.recipe ? 'No recipe' : p.canProduce ? 'Ready' : 'Short');
  downloadCSVSections(`production-plan-${new Date().toISOString().slice(0, 10)}.csv`, [
    {
      title: 'Items to make',
      headers: [
        ['Product', 'product'],
        ['Recipe', (p) => p.recipe?.name || ''],
        ['Kitchen orders', (p) => (p.requests || []).map((r) => r.code).join(' ')],
        ['Needed by', (p) => (p.needByDate ? String(p.needByDate).slice(0, 10) : '')],
        ['Urgency', 'urgency'],
        ['Unit', (p) => p.unit || 'kg'],
        ['Requested', (p) => qty3(p.ordered)],
        ['Made', (p) => qty3(p.made)],
        ['In progress', (p) => qty3(p.making)],
        ['Still to make', (p) => qty3(p.bulkKg)],
        ['Batches', (p) => p.batches ?? ''],
        ['Ingredients', status],
      ],
      rows: items,
    },
    {
      title: 'Raw materials required',
      headers: [
        ['Material', 'name'],
        ['Unit', 'unit'],
        ['Required', (r) => qty3(r.required)],
        ['Available', (r) => qty3(r.available)],
        ['Short', (r) => (r.shortfall > 0 ? qty3(r.shortfall) : '')],
        ['Made in-house', (r) => (r.madeByRecipeId ? 'Yes' : '')],   // short here → make more, don't buy
      ],
      rows: plan?.rawMaterials || [],
    },
  ]);
};

const OUTPUT_UNITS = ['kg', 'piece'];   // kitchen produces bulk in KGs or pieces only
const BASE_UNIT = { mass: 'kg', vol: 'l', count: 'piece' };   // a made raw material is made in its own family's unit
const QUICK_QTY = [5, 10, 25, 50];

// Client-side unit conversion mirroring the API's utils/units.js.
const TO_BASE = { kg: 1000, g: 1, l: 1000, ml: 1, piece: 1 };
const FAMILY = { kg: 'mass', g: 'mass', l: 'vol', ml: 'vol', piece: 'count' };
const convert = (qty, from, to) => {
  const q = Number(qty) || 0;
  if (!from || !to || from === to) return q;
  if (FAMILY[from] && FAMILY[from] === FAMILY[to]) return (q * TO_BASE[from]) / TO_BASE[to];
  return q;
};

// ── Start a batch ──────────────────────────────────────────────
const StartBatchModal = ({ recipes, kitchens, preset, onClose, onSaved }) => {
  const [recipeId, setRecipeId] = useState(preset?.recipeId ? String(preset.recipeId) : '');
  const [mode, setMode] = useState('output');           // 'output' | 'ingredient'
  const [qty, setQty] = useState(preset?.outputKg > 0 ? Number(preset.outputKg) : 10);   // output amount
  const [outputUnit, setOutputUnit] = useState('kg');
  const [primaryId, setPrimaryId] = useState('');        // driving raw material (recipeIngredient.rawMaterialId)
  const [primaryQty, setPrimaryQty] = useState('');      // amount of the main ingredient
  const [kitchenId, setKitchenId] = useState(kitchens[0]?.id || '');  // making happens at a Kitchen location
  const [req, setReq] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const recipe = recipes.find((r) => r.id === Number(recipeId));
  // A raw-material recipe (e.g. ghee from butter) makes raw stock in the material's own unit.
  const rawOut = recipe?.output || null;
  const sendUnit = rawOut ? (BASE_UNIT[FAMILY[rawOut.unit]] || 'kg') : 'kg';
  const pickable = preset?.raw ? recipes.filter((r) => r.rawMaterialId) : recipes;
  useEffect(() => { if (rawOut) setOutputUnit(sendUnit); }, [recipeId]); // eslint-disable-line react-hooks/exhaustive-deps
  const noRecipe = !recipeId && !!preset?.productId;   // making a product that has no recipe/BOM
  const maxKg = preset?.maxKg != null ? Number(preset.maxKg) : null;   // cap: can't make more than what's still required
  const ingredients = recipe?.ingredients || [];
  const baseYieldKg = recipe ? convert(recipe.baseYieldQty, recipe.baseYieldUnit || 'kg', 'kg') || 1 : 1;
  const primaryIng = ingredients.find((i) => i.rawMaterialId === Number(primaryId));

  // Default the driving ingredient to the recipe's primary (or the largest line).
  useEffect(() => {
    if (!ingredients.length) { setPrimaryId(''); return; }
    const main = ingredients.find((i) => i.isPrimary)
      || [...ingredients].sort((a, b) => Number(b.quantity) - Number(a.quantity))[0];
    setPrimaryId(main?.rawMaterialId || '');
    setPrimaryQty(main ? Number(main.quantity) : '');
  }, [recipeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve the batch to an output weight (kg) for both modes.
  const outputKg = useMemo(() => {
    if (mode === 'output') return convert(qty, outputUnit, 'kg');
    if (primaryIng && Number(primaryIng.quantity) > 0) {
      const scale = convert(primaryQty, primaryIng.unit, primaryIng.unit) / Number(primaryIng.quantity);
      // primaryQty is entered in the ingredient's own unit
      return baseYieldKg * scale;
    }
    return 0;
  }, [mode, qty, outputUnit, primaryQty, primaryIng, baseYieldKg]);

  // The kitchen may make MORE than requested (minimum batch sizes) — the surplus
  // becomes warehouse stock once accepted. maxKg is just the suggested amount.
  const surplus = maxKg != null && outputKg > maxKg + 1e-6 ? outputKg - maxKg : 0;

  // Keep the kitchen selected even if the locations list arrives after mount.
  useEffect(() => { if (!kitchenId && kitchens[0]) setKitchenId(String(kitchens[0].id)); }, [kitchens]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!recipeId || !(outputKg > 0)) { setReq(null); return; }
    // Location only affects the "available" numbers — resolve to the single/first kitchen.
    const loc = Number(kitchenId) || kitchens[0]?.id;
    const locQ = loc ? `&locationId=${loc}` : '';
    api.get(`/recipes/${recipeId}/requirements?qty=${outputKg}&unit=${sendUnit}${locQ}`)
      .then(({ data }) => setReq(data)).catch(() => setReq(null));
  }, [recipeId, kitchenId, outputKg, kitchens]);

  const submit = async () => {
    // Never send an empty kitchen — fall back to the single/first kitchen.
    const kid = Number(kitchenId) || kitchens[0]?.id;
    if (!kid) { setError('No kitchen location is configured'); return; }
    setBusy(true); setError('');
    try {
      // Bulk batch: send the resolved output weight in kg. Recipe-less runs send
      // productId instead of recipeId (nothing is consumed).
      const payload = { outputQty: Number(outputKg), outputUnit: sendUnit, kitchenLocationId: kid };
      if (recipeId) payload.recipeId = Number(recipeId);
      else payload.productId = Number(preset?.productId);
      const { data } = await api.post('/production', payload);
      const short = data.warnings?.shortfalls;
      toast.success(short?.length ? 'Batch started — raw stock is short, check inventory' : 'Batch started');
      onSaved(data.batch); onClose();
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Modal title={preset?.raw ? 'Make a raw material' : 'Start a batch'} onClose={onClose} wide
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={submit} disabled={busy || !(outputKg > 0)}>{busy ? 'Starting…' : 'Start making'}</button></>}>
      <div className="row">
        <div className="field" style={{ flex: 2 }}><label>What to make</label>
          {preset && !preset.raw
            ? <input value={recipe ? `${recipe.name}${recipe.product?.name ? ` (${recipe.product.name})` : ''}` : (preset.productName || 'Product')} disabled />
            : <select value={recipeId} onChange={(e) => setRecipeId(e.target.value)}>
                <option value="">— choose a recipe —</option>
                {pickable.map((r) => <option key={r.id} value={r.id}>{r.name}{(r.product || r.output)?.name ? ` (${(r.product || r.output).name})` : ''}</option>)}
              </select>}
        </div>
        {kitchens.length > 1 && (
          <div className="field"><label>Kitchen</label>
            <select value={kitchenId} onChange={(e) => setKitchenId(e.target.value)}>{kitchens.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}</select>
          </div>
        )}
      </div>

      {(recipe || noRecipe) && (
        <div className="field" style={{ marginBottom: '.5rem' }}>
          <label>How much to make {maxKg != null && <span className="muted small" style={{ fontWeight: 400 }}>· requested {num(maxKg)} kg (extra goes to warehouse)</span>}</label>
          {recipe && (
          <div className="quick-sizes" style={{ marginBottom: '.35rem' }}>
            <button type="button" className={`chip ${mode === 'output' ? 'chip-on' : ''}`} onClick={() => setMode('output')}>By amount</button>
            <button type="button" className={`chip ${mode === 'ingredient' ? 'chip-on' : ''}`} onClick={() => setMode('ingredient')}>By main ingredient</button>
          </div>
          )}
          {(mode === 'output' || noRecipe) ? (
            <div className="quick-sizes">
              {QUICK_QTY.map((n) => <button key={n} type="button" className={`chip ${Number(qty) === n ? 'chip-on' : ''}`} onClick={() => setQty(n)}>{n}</button>)}
              <input type="number" min="0" step="0.001" value={qty} onChange={(e) => setQty(e.target.value)} style={{ width: 100 }} />
              <select value={outputUnit} onChange={(e) => setOutputUnit(e.target.value)} style={{ width: 90 }}>
                {(rawOut ? [sendUnit] : OUTPUT_UNITS).map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          ) : (
            <div className="flex gap" style={{ alignItems: 'center' }}>
              <select value={primaryId} onChange={(e) => { setPrimaryId(e.target.value); const ing = ingredients.find((i) => i.rawMaterialId === Number(e.target.value)); setPrimaryQty(ing ? Number(ing.quantity) : ''); }} style={{ maxWidth: 260 }}>
                {ingredients.map((i) => <option key={i.rawMaterialId} value={i.rawMaterialId}>{i.rawMaterial?.name}{i.isPrimary ? ' ★' : ''}</option>)}
              </select>
              <input type="number" min="0" step="0.001" value={primaryQty} onChange={(e) => setPrimaryQty(e.target.value)} style={{ width: 110 }} autoFocus />
              <span className="muted">{primaryIng?.unit}</span>
            </div>
          )}
        </div>
      )}

      {recipe && (
        <div style={{ marginTop: '.6rem' }}>
          <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <strong className="small">Ingredients needed{outputKg > 0 ? ` — makes ${num(outputKg)} ${sendUnit}${rawOut ? ` of ${rawOut.name}` : ''}` : ''}</strong>
            {req && <span className={`badge ${req.canProduce ? 'badge-green' : 'badge-red'}`}>{req.canProduce ? 'Enough in kitchen' : 'Not enough'}</span>}
          </div>
          <div className="table-wrap" style={{ marginTop: '.3rem' }}>
            <table className="grid">
              <thead><tr><th>Ingredient</th><th className="right">Required</th><th className="right">Available</th><th className="right">Short</th></tr></thead>
              <tbody>
                {(req?.requirements || []).map((r, i) => (
                  <tr key={i} className={r.shortfall > 0 ? 'row-danger' : ''}>
                    <td>{r.shortfall > 0 ? '✕' : '✓'} {r.name}</td>
                    <td className="right">{num(r.required)} {r.unit}</td>
                    <td className="right">{num(r.available)}</td>
                    <td className="right">{r.shortfall > 0 ? <span className="neg">{num(r.shortfall)} {r.unit}</span> : '—'}</td>
                  </tr>
                ))}
                {req && !req.requirements.length && <tr><td colSpan={4} className="empty">No ingredients listed on this recipe.</td></tr>}
              </tbody>
            </table>
          </div>
          {req && !req.canProduce && <p className="muted small">Some items are short — you can still start; the shortfall shows as negative kitchen stock to reconcile later.</p>}
        </div>
      )}
      {preset?.raw && !pickable.length && <p className="muted small">No raw-material recipes yet — add one in Recipes / BOM and set <strong>Makes</strong> to Raw material.</p>}
      {rawOut && <p className="muted small">When marked done, it's added to kitchen stock as {rawOut.name}, and its cost updates the material's average cost.</p>}
      {noRecipe && <p className="muted small">No recipe for this product — nothing is consumed. Enter how much you're making; it lands as kitchen stock.</p>}
      {surplus > 0 && <p className="muted small">Making {num(surplus)} kg more than requested — the surplus will be added to warehouse stock.</p>}
      {error && <p className="form-error">{error}</p>}
    </Modal>
  );
};

// ── Mark done (one tap) ────────────────────────────────────────
const MarkDoneModal = ({ batch, onClose, onDone }) => {
  const unit = batch.outputUnit || 'kg';
  const [producedQty, setProducedQty] = useState(batch.outputQty);
  const [showWastage, setShowWastage] = useState(false);
  const [wastageQty, setWastageQty] = useState(0);
  const [expiryDate, setExpiryDate] = useState(shelfLifeToDate(batch.product?.shelfLife));
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');

  const submit = async () => {
    setBusy(true); setError('');
    try {
      await api.post(`/production/${batch.id}/ready`, { producedQty: Number(producedQty), wastageQty: Number(wastageQty) || 0, expiryDate: expiryDate || null });
      toast.success('Done — stock added'); onDone(); onClose();
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Modal title={`Mark done — ${batch.code}`} onClose={onClose}
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={submit} disabled={busy}>{busy ? 'Saving…' : '✓ Mark done'}</button></>}>
      <p className="muted" style={{ marginTop: 0 }}>{batch.product?.name || batch.rawMaterial?.name} · in {unit}</p>
      <div className="field">
        <label>How much did you make? ({unit})</label>
        <input type="number" step="0.001" value={producedQty} onChange={(e) => setProducedQty(e.target.value)} style={{ fontSize: '1.2rem', fontWeight: 700 }} autoFocus />
        <span className="muted small">Planned {num(batch.outputQty)} {unit} — change only if the yield was different.</span>
      </div>
      <div className="field">
        <label>Best-before date</label>
        <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
        {batch.product?.shelfLife && <span className="muted small">Auto-filled from shelf life ({batch.product.shelfLife}).</span>}
      </div>
      {showWastage ? (
        <div className="field"><label>Wastage ({unit})</label><input type="number" step="0.001" value={wastageQty} onChange={(e) => setWastageQty(e.target.value)} /></div>
      ) : (
        <button type="button" className="chip" onClick={() => setShowWastage(true)}>+ Record wastage</button>
      )}
      {error && <p className="form-error">{error}</p>}
    </Modal>
  );
};


const dispoBadge = (d) => ({ reprocess: 'badge-gold', waste: 'badge-red', restock: 'badge-green' }[d] || 'badge-gray');
const dispoLabel = (d) => ({ reprocess: 'Reprocess → kitchen', waste: 'Waste → damaged', restock: 'Restock → warehouse' }[d] || (d || '—'));

// Right-side slider: the clicked return's own items + how THIS return was used.
const ReturnDrawer = ({ id, history, usage, onClose }) => {
  const lines = history.filter((h) => h.returnId === id);
  const used = usage.filter((u) => u.returnId === id);
  const head = lines[0] || {};
  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className="side-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="side-drawer-head">
          <h3 style={{ margin: 0 }}>Return {head.returnCode}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <p className="muted small" style={{ marginTop: 0 }}>{head.branch}{head.when ? ` · ${dt(head.when)}` : ''}</p>

        <div className="side-card-title" style={{ marginTop: '1rem' }}>Returned items</div>
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th>Item</th><th className="right">Qty</th><th>Reason</th><th>Disposition</th></tr></thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td>{l.product} <span className="muted">{l.variant}</span></td>
                  <td className="right">{num(l.qty)} {l.unit}</td>
                  <td className="muted">{l.reason || '—'}</td>
                  <td><span className={`badge ${dispoBadge(l.disposition)}`}>{dispoLabel(l.disposition)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="side-card-title" style={{ marginTop: '1rem' }}>How this return was used</div>
        {used.length === 0 ? <p className="muted small">Not reworked or scrapped yet.</p> : (
          <div className="table-wrap">
            <table className="grid">
              <thead><tr><th>When</th><th>Action</th><th>Item</th><th className="right">Qty</th></tr></thead>
              <tbody>
                {used.map((u, i) => (
                  <tr key={i}>
                    <td className="muted">{dt(u.when)}</td>
                    <td><span className={`badge ${u.type === 'scrapped' ? 'badge-red' : 'badge-green'}`}>{u.type === 'scrapped' ? 'Scrapped' : 'Reworked'}</span></td>
                    <td>{u.product} <span className="muted">{u.variant}</span></td>
                    <td className="right">{num(u.qty)} {u.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const Production = () => {
  const { can } = useAuth();
  const [batches, setBatches] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [kitchens, setKitchens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startPreset, setStartPreset] = useState(null);   // start a batch straight from an items-to-prepare row
  const [done, setDone] = useState(null);
  const [dashDate] = useState(() => new Date().toISOString().slice(0, 10));   // dashboard always shows today
  const [dash, setDash] = useState(null);
  const [plan, setPlan] = useState(null);
  const [reprocess, setReprocess] = useState([]);
  const [reprocessHistory, setReprocessHistory] = useState([]);
  const [reprocessUsage, setReprocessUsage] = useState([]);
  const [peek, setPeek] = useState(null);          // returnId whose lines are shown in a modal
  const [drawQty, setDrawQty] = useState({});     // reprocess row index -> qty to draw
  const [showReturns, setShowReturns] = useState(false);   // "View All Rework" modal
  const [progAll, setProgAll] = useState(false);
  const [doneAll, setDoneAll] = useState(false);

  const loadDash = () => api.get(`/reports/production-dashboard?date=${dashDate}`).then(({ data }) => setDash(data)).catch(() => {});
  const loadPlan = () => api.get('/reports/production-plan-detail').then(({ data }) => setPlan(data)).catch(() => setPlan({ products: [], rawMaterials: [], totals: {} }));
  const load = () => { setLoading(true); api.get('/production').then(({ data }) => setBatches(data.batches || [])).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false)); loadDash(); loadPlan(); api.get('/production/reprocess').then(({ data }) => { setReprocess(data.items || []); setReprocessHistory(data.history || []); setReprocessUsage(data.usage || []); }).catch(() => {}); };
  useEffect(() => {
    load();
    api.get('/recipes').then(({ data }) => setRecipes(data.recipes || []));
    api.get('/locations').then(({ data }) => setKitchens((data.locations || []).filter((l) => l.type === 'kitchen')));
  }, []);
  useEffect(() => { loadDash(); }, [dashDate]);

  const canWrite = can('production:write');
  const drawReprocess = async (r, i) => {
    const qty = Number(drawQty[i] != null ? drawQty[i] : r.qty);
    if (!(qty > 0)) return toast.error('Enter a quantity to draw');
    try {
      const { data } = await api.post('/production/reprocess/draw', { lines: [{ productId: r.productId, variantId: r.variantId, qty, unit: r.unit }] });
      toast.success(`Reworked ${num(data.bulkKg)} kg of ${r.product} into stock`);
      setDrawQty({}); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const scrapReprocess = async (r, i) => {
    const qty = Number(drawQty[i] != null ? drawQty[i] : r.qty);
    if (!(qty > 0)) return toast.error('Enter a quantity to scrap');
    if (!window.confirm(`Scrap ${qty} ${r.unit} of ${r.product} ${r.variant} as damaged?`)) return;
    try {
      await api.post('/production/reprocess/scrap', { lines: [{ productId: r.productId, variantId: r.variantId, qty, unit: r.unit }] });
      toast.success(`Scrapped ${num(qty)} ${r.unit} as damaged`);
      setDrawQty({}); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const inProgress = batches.filter((b) => b.status === 'in_production');
  const deliveredBatches = batches.filter((b) => b.status === 'ready' && String(b.readyAt || '').slice(0, 10) === dashDate);

  // Derived figures for the bottom summary bar.
  const planProducts = plan?.products || [];
  const pendingProducts = planProducts.filter((p) => Number(p.bulkKg) > 0);   // still to make
  const totalPlanned = planProducts.reduce((s, p) => s + Number(p.ordered || 0), 0);
  const totalMade = planProducts.reduce((s, p) => s + Number(p.made || 0), 0);
  const overallPct = totalPlanned > 0 ? Math.round((totalMade / totalPlanned) * 100) : 0;
  const delayed = planProducts.filter((p) => p.urgency === 'overdue').length;
  const onTime = planProducts.filter((p) => p.urgency && p.urgency !== 'overdue' && p.urgency !== 'none').length;

  return (
    <div className="prod-dash">
      {/* Summary tiles */}
      {dash && (
        <div className="prod-stats">
          <div className="pstat">
            <div className="pstat-ico pstat-ico--orange"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="6" y="4" width="12" height="17" rx="2"/><path d="M9 4a3 3 0 0 1 6 0M9 11h6M9 15h4"/></svg></div>
            <div><div className="pstat-label">Pending to prepare</div><div className="pstat-value orange">{num(dash.pending.bulkKg)} kg</div><div className="pstat-sub">{dash.pending.products} product{dash.pending.products === 1 ? '' : 's'} · from open requests</div></div>
          </div>
          <div className="pstat">
            <div className="pstat-ico pstat-ico--green"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M5 11h14l-1 8a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2z"/><path d="M4 11h16"/><path d="M8 11c0-4 8-4 8 0"/></svg></div>
            <div><div className="pstat-label">In progress</div><div className="pstat-value">{dash.inProgress.count}</div><div className="pstat-sub">batch{dash.inProgress.count === 1 ? '' : 'es'} being made</div></div>
          </div>
          <div className="pstat">
            <div className="pstat-ico pstat-ico--green"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9"/><path d="M8 12l3 3 5-6"/></svg></div>
            <div><div className="pstat-label">Delivered today</div><div className="pstat-value">{dash.delivered.count}</div><div className="pstat-sub">{num(dash.delivered.producedQty)} made on {dash.date}</div></div>
          </div>
          <div className="pstat">
            <div className="pstat-ico pstat-ico--red"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 12a9 9 0 1 1-3-6.7"/><path d="M21 4v4h-4"/></svg></div>
            <div><div className="pstat-label">Returned to rework</div><div className="pstat-value red">{reprocess.length}</div><div className="pstat-sub">item{reprocess.length === 1 ? '' : 's'} from the warehouse</div></div>
          </div>
        </div>
      )}

      {loading && <div className="spinner" />}
      {canWrite && (
        <div className="flex" style={{ justifyContent: 'flex-end', margin: '.2rem 0 .6rem' }}>
          <button className="btn btn-outline btn-sm" onClick={() => setStartPreset({ raw: true })} title="Make a raw material from other raw materials, e.g. ghee from butter">+ Make raw material</button>
        </div>
      )}

      {/* 4-column board */}
      <div className="prod-board">
        <div className="pcol">
          <div className="pcol-head">
            <h3>Pending to make</h3>
            <div className="flex" style={{ gap: 6, alignItems: 'center' }}>
              <button className="btn btn-outline btn-sm" onClick={() => exportPlanCSV(plan)} disabled={!pendingProducts.length} title="Download items to make and raw materials required (CSV)">⭳ Download</button>
              <span className="pcol-count">{pendingProducts.length}</span>
            </div>
          </div>
          <PlanItems plan={plan} onStart={(p, remaining) => setStartPreset({ recipeId: p.recipe?.id, productId: p.productId, productName: p.product, outputKg: remaining, maxKg: remaining })} canStart={canWrite} />
        </div>

        <div className="pcol">
          <div className="pcol-head"><h3>In progress</h3><span className="pcol-count">{inProgress.length}</span></div>
          <div className="pcol-stack">
            {(progAll ? inProgress : inProgress.slice(0, 3)).map((b) => <ProgressCard key={b.id} b={b} canWrite={canWrite} onDone={setDone} />)}
            {!inProgress.length && <div className="pcol-empty">Nothing being made right now.</div>}
          </div>
          {inProgress.length > 3 && <button className="pcol-viewall" onClick={() => setProgAll((s) => !s)}>{progAll ? 'Show less' : `View All In Progress (${inProgress.length})`} <IconChevron /></button>}
        </div>

        <div className="pcol">
          <div className="pcol-head"><h3>Completed today</h3><span className="pcol-count">{deliveredBatches.length}</span></div>
          <div className="pcol-stack">
            {(doneAll ? deliveredBatches : deliveredBatches.slice(0, 3)).map((b) => <CompletedCard key={b.id} b={b} />)}
            {!deliveredBatches.length && <div className="pcol-empty">Nothing completed on {day(dashDate)}.</div>}
          </div>
          {deliveredBatches.length > 3 && <button className="pcol-viewall" onClick={() => setDoneAll((s) => !s)}>{doneAll ? 'Show less' : `View All Completed (${deliveredBatches.length})`} <IconChevron /></button>}
        </div>

        <div className="pcol">
          <div className="pcol-head"><h3>Returned to rework</h3><span className="pcol-count">{reprocess.length}</span></div>
          <div className="pcol-stack">
            {reprocess.slice(0, 3).map((r, i) => <ReworkCard key={i} r={r} />)}
            {!reprocess.length && <div className="pcol-empty">Nothing returned to rework.</div>}
          </div>
          {(reprocess.length > 0 || reprocessHistory.length > 0) && <button className="pcol-viewall rework" onClick={() => setShowReturns(true)}>View All Rework <IconChevron /></button>}
        </div>
      </div>

      {/* Bottom summary bar */}
      <div className="prod-foot">
        <div className="pfoot-item"><svg viewBox="0 0 24 24" fill="none" stroke="var(--pg)" strokeWidth="1.8"><path d="M12 3v9l7 4"/><circle cx="12" cy="12" r="9"/></svg><div><div className="pfoot-label">Total Planned</div><div className="pfoot-value">{num(totalPlanned)} kg</div></div></div>
        <div className="pfoot-item"><svg viewBox="0 0 24 24" fill="none" stroke="var(--pg)" strokeWidth="1.8"><path d="M5 11h14l-1 8a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2z"/><path d="M8 11c0-4 8-4 8 0"/></svg><div><div className="pfoot-label">Total Made</div><div className="pfoot-value">{num(totalMade)} kg</div></div></div>
        <div className="pfoot-progress"><span className="pfoot-label">Overall Progress</span><div className="pbar"><div className="pbar-fill" style={{ width: `${overallPct}%` }} /></div><strong style={{ color: 'var(--pg-deep)' }}>{overallPct}%</strong></div>
        <div className="pfoot-item"><svg viewBox="0 0 24 24" fill="none" stroke="#d98324" strokeWidth="1.8"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg><div><div className="pfoot-label">On Time</div><div className="pfoot-value">{onTime}</div></div></div>
        <div className="pfoot-item"><svg viewBox="0 0 24 24" fill="none" stroke="var(--pred)" strokeWidth="1.8"><path d="M12 3 2 20h20z"/><path d="M12 9v5M12 17h.01"/></svg><div><div className="pfoot-label">Delayed</div><div className="pfoot-value">{delayed}</div></div></div>
        <Link to="/reports" className="btn btn-primary" style={{ marginLeft: 'auto' }}>View All Reports</Link>
      </div>

      {/* View-all rework modal (draw / scrap + history) */}
      {showReturns && (
        <Modal title="Returned to rework" onClose={() => setShowReturns(false)} wide
          footer={<button className="btn btn-outline" onClick={() => setShowReturns(false)}>Close</button>}>
          {reprocess.length === 0 && reprocessHistory.length === 0 && <p className="muted">Nothing returned for reprocessing.</p>}
          {reprocess.length > 0 && (<>
            <strong className="small" style={{ display: 'block', marginBottom: '.4rem' }}>To rework</strong>
            <div className="table-wrap">
              <table className="grid">
                <thead><tr><th>Product</th><th>Pack</th><th className="right">Qty</th><th>Reason</th><th>From branch</th>{canWrite && <th className="right">Draw &amp; rework</th>}</tr></thead>
                <tbody>
                  {reprocess.map((r, i) => (
                    <tr key={i}>
                      <td>{r.product}</td>
                      <td className="muted">{r.variant}</td>
                      <td className="right">{num(r.qty)} {r.unit}</td>
                      <td className="muted">{r.reason || '—'}</td>
                      <td>{r.branch}</td>
                      {canWrite && (
                        <td className="right">
                          <div className="flex" style={{ gap: 6, alignItems: 'center', justifyContent: 'flex-end' }}>
                            <input type="number" min="0" max={r.qty} step="0.001" style={{ width: 80 }} value={drawQty[i] != null ? drawQty[i] : r.qty} onChange={(e) => setDrawQty((p) => ({ ...p, [i]: e.target.value }))} />
                            <button className="btn btn-gold btn-sm" onClick={() => drawReprocess(r, i)}>Draw</button>
                            <button className="btn btn-danger btn-sm" onClick={() => scrapReprocess(r, i)} title="Write off as damaged">Scrap</button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>)}
          {reprocessHistory.length > 0 && (<>
            <strong className="small" style={{ display: 'block', margin: '1rem 0 .4rem' }}>All returned items <span className="muted" style={{ fontWeight: 400 }}>(click for details)</span></strong>
            <div className="table-wrap">
              <table className="grid">
                <thead><tr><th>Return</th><th>Product</th><th>Pack</th><th className="right">Qty</th><th>From branch</th><th className="right">Returned</th></tr></thead>
                <tbody>
                  {reprocessHistory.map((r, i) => (
                    <tr key={i} style={{ cursor: 'pointer' }} onClick={() => setPeek(r.returnId)} title="Open return">
                      <td className="mono muted">{r.returnCode}</td>
                      <td>{r.product}</td>
                      <td className="muted">{r.variant}</td>
                      <td className="right">{num(r.qty)} {r.unit}</td>
                      <td>{r.branch}</td>
                      <td className="muted right">{r.when ? dt(r.when) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>)}
        </Modal>
      )}

      {peek && <ReturnDrawer id={peek} history={reprocessHistory} usage={reprocessUsage} onClose={() => setPeek(null)} />}
      {startPreset && <StartBatchModal recipes={recipes} kitchens={kitchens} preset={startPreset} onClose={() => setStartPreset(null)} onSaved={load} />}
      {done && <MarkDoneModal batch={done} onClose={() => setDone(null)} onDone={load} />}
    </div>
  );
};

export default Production;
